<template>
  <div class="profile-stats">
    <div class="cabinet-card">
      <h2 class="cabinet-card__title">{{ t("title") }}</h2>
      <v-skeleton-loader v-if="!data" class="mt-6" type="table-row@2"></v-skeleton-loader>
      <template v-else>
        <div class="profile-stats__grid">
          <div class="profile-stats__metric">
            <div class="profile-stats__label">{{ t("views") }}:</div>
            <div class="profile-stats__value">{{ data.stats.this_month.views }}</div>
            <div class="profile-stats__sub">{{ t("thisMonth") }}</div>
            <div class="profile-stats__total">{{ t("total") }}: {{ data.stats.total.views }}</div>
          </div>
          <div class="profile-stats__metric">
            <div class="profile-stats__label">{{ t("contactsViews") }}:</div>
            <div class="profile-stats__value">{{ data.stats.this_month.contacts_clicks }}</div>
            <div class="profile-stats__sub">{{ t("thisMonth") }}</div>
            <div class="profile-stats__total">{{ t("total") }}: {{ data.stats.total.contacts_clicks }}</div>
          </div>
          <div v-if="data.leads" class="profile-stats__metric">
            <div class="profile-stats__label">Заявки з Платформи:</div>
            <div class="profile-stats__value">{{ data.leads.month.total }}</div>
            <div class="profile-stats__sub">{{ t("thisMonth") }}</div>
            <div class="profile-stats__total">{{ t("total") }}: {{ data.leads.all.total }}, консультацій: {{ data.leads.all.scheduled }}</div>
          </div>
          <div class="profile-stats__metric">
            <div class="profile-stats__label">{{ t("favorites") }}:</div>
            <div class="profile-stats__value">{{ data.favorites || 0 }}</div>
            <div class="profile-stats__sub">{{ t("favoritesSub") }}</div>
          </div>
        </div>

        <div class="profile-stats__chart" :aria-label="t('lastDays')">
          <div class="profile-stats__bars">
            <div
              v-for="day in data.stats.daily"
              :key="day.date"
              class="profile-stats__bar"
              :title="`${day.date}: ${day.views} / ${day.contacts_clicks}`"
            >
              <span class="profile-stats__bar-views" :style="{ height: barHeight(day.views) }"></span>
              <span class="profile-stats__bar-clicks" :style="{ height: barHeight(day.contacts_clicks) }"></span>
            </div>
          </div>
          <div class="d-flex profile-stats__legend">
            <span><i class="dot dot--views"></i>{{ t("views").toLowerCase() }}</span>
            <span><i class="dot dot--clicks"></i>{{ t("contactsViews").toLowerCase() }}</span>
          </div>
        </div>
      </template>
    </div>

    <div class="cabinet-card profile-status">
      <h2 class="cabinet-card__title">{{ t("statusTitle") }}</h2>
      <v-skeleton-loader v-if="!data" class="mt-6" type="list-item-two-line"></v-skeleton-loader>
      <template v-else>
        <div class="profile-status__state" :class="{ 'profile-status__state--off': !subscription.active }">
          <span>{{ subscription.active ? t("statusActive") : t("statusInactive") }}</span>
          <i class="profile-status__dot"></i>
        </div>
        <div class="profile-status__caption">{{ subscription.active ? t("notEnforced") : t("hiddenFromCatalog") }}</div>
        <div class="profile-status__next">
          <span>{{ t("nextPayment") }}</span>
          <span v-if="subscription.paid_until">{{ formatDate(subscription.paid_until) }}</span>
          <span v-else>—</span>
        </div>
        <div v-if="subscription.paid" class="profile-status__days">
          <span>{{ t("daysLeft") }}:</span>
          <span>{{ subscription.days_left }}</span>
        </div>
        <a class="profile-status__manage" :href="manageUrl" target="_blank" rel="noopener">{{ t("manageSubscription") }}</a>
        <p v-if="data.unsubscribed_at" class="profile-status__unsubscribed">
          Ви скасували підписку {{ formatDate(data.unsubscribed_at.slice(0, 10)) }}. Анкета показується до кінця оплаченого періоду, потім іде в архів.
        </p>
        <button v-else type="button" class="profile-status__cancel" @click="cancelDialog = true">Скасувати підписку</button>
      </template>
    </div>

    <LeadsCard />

    <v-dialog v-model="cancelDialog" max-width="480">
      <v-card>
        <v-card-title class="profile-status__dialog-title">Скасувати підписку</v-card-title>
        <v-card-text>
          <p>Анкета залишиться в каталозі до кінця оплаченого періоду. Напишіть, будь ласка, чому ви йдете: це обов'язково.</p>
          <v-textarea
            v-model="cancelReason"
            label="Причина"
            outlined
            rows="3"
            auto-grow
            counter="1000"
            :error-messages="cancelError"
          ></v-textarea>
          <p class="caption mb-0">Щомісячне списання зупиняється в кабінеті WayForPay: <a :href="manageUrl" target="_blank" rel="noopener">{{ manageUrl }}</a></p>
        </v-card-text>
        <v-card-actions class="profile-status__dialog-actions">
          <v-btn text @click="cancelDialog = false">Залишитися</v-btn>
          <v-btn color="primary" depressed :loading="cancelling" @click="cancelSubscription">Скасувати підписку</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RoomServices from "@/services/api/room.services";
import ApiClient from "@/libs/http-client";
const LeadsCard = () => import("./LeadsCard");

// WayForPay account where a psychologist manages the monthly subscription (link from the client's brief)
const SUBSCRIPTION_MANAGE_URL = "https://m.wayforpay.com/account/site/login";

// Same rule as the backend: words, not "……" or "аааа" - 5+ letters, 3+ different, letters are most of the text
const isMeaningfulReason = (reason) => {
  const chars = reason.replace(/\s+/g, "");
  const letters = (chars.match(/\p{L}/gu) || []).map((letter) => letter.toLowerCase());
  return letters.length >= 5 && new Set(letters).size >= 3 && letters.length * 2 >= [...chars].length;
};

export default {
  name: "ProfileStats",
  components: { LeadsCard },
  data() {
    return {
      data: null,
      manageUrl: SUBSCRIPTION_MANAGE_URL,
      cancelDialog: false,
      cancelReason: "",
      cancelError: "",
      cancelling: false,
    };
  },
  computed: {
    subscription() {
      return (this.data && this.data.subscription) || {};
    },
    maxValue() {
      if (!this.data) return 1;
      return Math.max(1, ...this.data.stats.daily.map((day) => day.views));
    },
  },
  mounted() {
    RoomServices.myStats(30).then((response) => {
      this.data = response.data.data;
    });
  },
  methods: {
    t(key) {
      return this.$vuetify.lang.t(`$vuetify.main.psychologistDashboard.stats.${key}`);
    },
    cancelSubscription() {
      const reason = this.cancelReason.trim();
      if (!isMeaningfulReason(reason)) {
        this.cancelError = "Напишіть причину словами, щонайменше 5 літер (не крапки чи символи)";
        return;
      }
      this.cancelError = "";
      this.cancelling = true;
      ApiClient.post("api/psychologists/me/unsubscribe", { reason })
        .then((response) => {
          if (!response) return;
          this.data = { ...this.data, unsubscribed_at: response.data.data.unsubscribed_at };
          this.cancelDialog = false;
        })
        .finally(() => {
          this.cancelling = false;
        });
    },
    barHeight(value) {
      return `${Math.round((value / this.maxValue) * 100)}%`;
    },
    formatDate(value) {
      const [date] = String(value).split(" ");
      return date.split("-").reverse().join(".");
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-stats {
  display: flex;
  flex-direction: column;
  row-gap: 20px;
}
.profile-status__cancel {
  display: block;
  margin-top: 16px;
  color: #8A8A8A;
  font-size: 14px;
  text-decoration: underline;
}
.profile-status__unsubscribed {
  margin: 16px 0 0;
  color: #8A8A8A;
  font-size: 14px;
}
.profile-status__dialog-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  .v-btn {
    margin-left: 0 !important;
  }
}
.profile-status__dialog-title {
  word-break: break-word;
}
.profile-stats__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  margin-top: 34px;
}
.profile-stats__metric {
  padding: 24px 10px 10px;
  text-align: center;
  background: #FFFFFF;
  border: 1px solid #00A3B7;
  border-radius: 6px;
}
.profile-stats__label {
  max-width: 110px;
  margin: 0 auto;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  color: #575756;
}
.profile-stats__value {
  margin-top: 20px;
  font-size: 36px;
  font-weight: 500;
  line-height: 1;
  color: #00A3B7;
}
.profile-stats__sub {
  font-size: 14px;
  color: #575756;
}
.profile-stats__total {
  margin-top: 30px;
  font-size: 14px;
  color: #8A8A8A;
}
.profile-stats__chart {
  margin-top: 60px;
}
.profile-stats__bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 64px;
  border-bottom: 1px solid #C9D6E0;
}
.profile-stats__bar {
  position: relative;
  flex: 1;
  height: 100%;
}
.profile-stats__bar-views,
.profile-stats__bar-clicks {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
.profile-stats__bar-views {
  background: #A5D8E0;
}
.profile-stats__bar-clicks {
  background: #00A3B7;
}
.profile-stats__legend {
  flex-wrap: wrap;
  gap: 32px;
  margin-top: 4px;
  font-size: 14px;
  color: #8A8A8A;
}
.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 8px;
}
.dot--views {
  background: #A5D8E0;
}
.dot--clicks {
  background: #00A3B7;
}
.profile-status__state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 22px;
  padding: 12px 12px 12px 20px;
  font-size: 20px;
  font-weight: 500;
  color: #00A3B7;
  background: #DCE5EC;
  border: 1px solid #C9D6E0;
  border-radius: 4px;
}
.profile-status__state--off {
  color: #575756;
}
.profile-status__dot {
  width: 26px;
  height: 26px;
  background: #7DC592;
  border: 6px solid #FFFFFF;
  border-radius: 50%;
}
.profile-status__state--off .profile-status__dot {
  background: #E57373;
}
.profile-status__caption {
  margin-top: 6px;
  font-size: 14px;
  color: #8A8A8A;
  text-align: right;
}
.profile-status__next {
  display: flex;
  column-gap: 10px;
  margin-top: 60px;
  padding-bottom: 8px;
  font-size: 16px;
  color: #575756;
  border-bottom: 1px solid #C9D6E0;
}
.profile-status__days {
  display: flex;
  column-gap: 10px;
  margin-top: 8px;
  padding-bottom: 8px;
  font-size: 16px;
  color: #575756;
  border-bottom: 1px solid #C9D6E0;
}
.profile-status__manage {
  display: block;
  margin-top: 8px;
  font-size: 14px;
  color: #8A8A8A !important;
  text-align: right;
  text-decoration: none;
}
.profile-status__manage:hover {
  color: #00A3B7 !important;
}
</style>
